import { ref, Ref, computed } from 'vue'
import api from '@/api'
import { QueueStatusResponse } from '@/classes/WhatsApp'
import util from '@/helpers/util'

export function useWhatsAppQueue(
    engineRunning: Ref<boolean>,
    getAccountName: (accountId: string) => string,
    getAccountPhoneNumber: (accountId: string) => string | undefined,
    getGroupName: (groupJid: string) => string,
    openConfigurations?: Ref<Array<any>>
) {
    const queueStatus = ref<QueueStatusResponse>({})
    const queueProcessingEnabled = ref(false)
    const isStartingQueue = ref(false)
    const isStoppingQueue = ref(false)
    const removingFromQueue = ref<Record<string, boolean>>({})

    const queueItemsList = computed(() => {
        const items: Array<{
            accountId: string
            accountName: string
            accountPhoneNumber?: string
            groupName: string
            groupSubject: string
            message: string
            configurationName: string | null
            id: string
            status: string
        }> = []

        Object.entries(queueStatus.value).forEach(([accountId, queueItems]) => {
            queueItems.forEach((item) => {
                let groupSubject = ''
                if (item.configurationName && openConfigurations) {
                    const config = openConfigurations.value.find(c => c.name === item.configurationName)
                    if (config) {
                        const accountGroup = config.groups.find((g: any) => g.jid === item.groupJid)
                        if (accountGroup) {
                            groupSubject = accountGroup.subject
                        }
                    }
                }

                items.push({
                    accountId,
                    accountName: getAccountName(accountId),
                    accountPhoneNumber: item.accountPhoneNumber || getAccountPhoneNumber(accountId),
                    groupName: getGroupName(item.groupJid),
                    groupSubject: groupSubject,
                    message: item.message,
                    configurationName: item.configurationName || null,
                    id: item.id,
                    status: item.status || 'pending'
                })
            })
        })

        return items
    })

    const refreshQueueStatus = async () => {
        if (!engineRunning.value) {
            return
        }
        try {
            const response = await api.whatsapp.getQueueStatus()
            queueStatus.value = response.data as QueueStatusResponse
        } catch (error) {
            console.error('Failed to refresh queue status:', error)
        }
    }

    const startQueue = async () => {
        isStartingQueue.value = true
        try {
            await api.whatsapp.startQueue()
            queueProcessingEnabled.value = true
        } catch (error: any) {
            console.error('Failed to start queue:', error)
            const errorMessage = error.response?.data?.error || 'Failed to start queue. Please try again.'
            util.showToast(errorMessage, { icon: 'error', iconClass: 'toast-icon-circle' })
        } finally {
            isStartingQueue.value = false
        }
    }

    const stopQueue = async () => {
        isStoppingQueue.value = true
        try {
            await api.whatsapp.stopQueue()
            queueProcessingEnabled.value = false
        } catch (error) {
            console.error('Failed to stop queue:', error)
            util.showToast('Failed to stop queue. Please try again.', { icon: 'error', iconClass: 'toast-icon-circle' })
        } finally {
            isStoppingQueue.value = false
        }
    }

    const clearQueue = async () => {
        if (!confirm('Are you sure you want to clear all queued messages? This action cannot be undone.')) {
            return
        }
        try {
            const response = await api.whatsapp.clearQueue()
            queueStatus.value = {}
            util.showToast(`Queue cleared successfully. ${response.data.count || 0} message(s) removed.`, { icon: 'success', iconClass: 'toast-icon-circle' })
        } catch (error) {
            console.error('Failed to clear queue:', error)
            util.showToast('Failed to clear queue. Please try again.', { icon: 'error', iconClass: 'toast-icon-circle' })
        }
    }

    const removeFromQueue = async (accountId: string, id: string) => {
        if (!confirm('Remove this item from the queue?')) return
        const itemKey = `${accountId}-${id}`
        removingFromQueue.value[itemKey] = true
        try {
            await api.whatsapp.removeFromQueue(accountId, id)
            await refreshQueueStatus()
        } catch (error) {
            console.error('Failed to remove from queue:', error)
            util.showToast('Failed to remove item from queue', { icon: 'error', iconClass: 'toast-icon-circle' })
        } finally {
            removingFromQueue.value[itemKey] = false
        }
    }

    const updateQueueStatusFromData = (queueStatusData: QueueStatusResponse | null | undefined, queueProcessingEnabledData?: boolean) => {
        if (queueStatusData != null && typeof queueStatusData === 'object') {
            queueStatus.value = queueStatusData
        } else {
            queueStatus.value = {}
        }
        if (queueProcessingEnabledData !== undefined) {
            queueProcessingEnabled.value = queueProcessingEnabledData
        }
    }

    return {
        queueStatus,
        queueProcessingEnabled,
        isStartingQueue,
        isStoppingQueue,
        removingFromQueue,
        queueItemsList,
        refreshQueueStatus,
        startQueue,
        stopQueue,
        clearQueue,
        removeFromQueue,
        updateQueueStatusFromData
    }
}

